/**
 * B126(b) — the tenant's words, carried by context. The domain owns the
 * packs and the ONE resolver (vocabularyFor); this module is only the
 * plumbing: an app mounts VocabularyProvider with the tenant record's
 * stored type, and every @transact/ui component (and every app surface)
 * reads words through useVocabulary(). No provider resolves to GENERIC
 * neutral commerce words — the shared package never guesses ferry.
 */
import { createContext, useContext, type ReactNode } from "react";

/* B403 #22-extension — re-exported so buyer apps (storefront/embed) reach
   the packs through their existing @transact/ui edge; domain stays the
   single owner of the words. */
export { GENERIC_VOCABULARY, TENANT_TYPES, VOCABULARY_PACKS, isTenantType, vocabularyFor } from "@transact/domain";
export type { TenantType, VocabularyPack } from "@transact/domain";
import { GENERIC_VOCABULARY, vocabularyFor, type TenantType, type VocabularyPack } from "@transact/domain";

const VocabularyContext = createContext<VocabularyPack>(GENERIC_VOCABULARY);

export interface VocabularyProviderProps {
  /** The tenant record's stored type; undefined = not chosen yet (GENERIC). */
  readonly tenantType: TenantType | undefined;
  readonly children: ReactNode;
}

/** Mounted once per app, fed from ops.config.get's tenantType. */
export function VocabularyProvider({ tenantType, children }: VocabularyProviderProps) {
  return <VocabularyContext.Provider value={vocabularyFor(tenantType)}>{children}</VocabularyContext.Provider>;
}

/** The only door from a component to industry words. */
export function useVocabulary(): VocabularyPack {
  return useContext(VocabularyContext);
}

/** Heading case without the caller owning words: "departures" → "Departures". */
export function cap(word: string): string {
  return word.length === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1);
}
